import React, { useEffect, useMemo, useRef, useState } from "react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import researchService from "../services/researchService";
import authService from "../services/authService";
import { FaDownload, FaSearch } from "react-icons/fa";
import { notifyAndRedirectToLogin } from "../utils/authRedirect";
import DATASETS from "../constants/DataSet";
import datasetViewConfig from "../features/data-view/config/datasetViewConfig";
import {
  DataViewDetailPanelToggle,
  DatasetRecordTable,
  DatasetTabs,
  RecordDetailCard,
} from "../features/data-view/components";
import { DETAIL_PANEL_POSITIONS } from "../constants/DataView.constants";
import { useDataViewSelection, useDataViewState } from "../features/data-view/hooks";
import {
  buildSemanticViewModels,
  getDownloadableResultFromNormalized,
  normalizeSemanticResults,
} from "./semanticSearchAdapter";

const SEMANTIC_DATASETS = DATASETS.filter((dataset) => dataset.id !== "company");

const EnhancedResearchDashboard = () => {
  const [activeTab, setActiveTab] = useState(SEMANTIC_DATASETS[0]?.id || "clinical_trials");
  const [searchQuery, setSearchQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [searchResults, setSearchResults] = useState({});
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const searchAbortControllerRef = useRef(null);
  const latestSearchRequestRef = useRef(0);
  const {
    isDetailPanelOpen,
    setIsDetailPanelOpen,
    detailPanelPosition,
    setDetailPanelPosition,
  } = useDataViewState({
    initialDetailPanelPosition: DETAIL_PANEL_POSITIONS.RIGHT,
  });

  const currentResults = useMemo(
    () => searchResults[activeTab] || [],
    [activeTab, searchResults]
  );
  const normalizedResults = useMemo(
    () => normalizeSemanticResults(activeTab, currentResults),
    [activeTab, currentResults]
  );
  const { selectedRowIds, toggleRow, toggleAll, setSelectedRowIds } =
    useDataViewSelection(normalizedResults);
  const activeConfig = datasetViewConfig[activeTab] || datasetViewConfig.clinical_trials;
  const { rowModels, detailModelsById } = useMemo(
    () => buildSemanticViewModels(activeTab, normalizedResults),
    [activeTab, normalizedResults]
  );
  const selectedRows = useMemo(
    () => normalizedResults.filter((record) => selectedRowIds.includes(record._record_id)),
    [normalizedResults, selectedRowIds]
  );
  const selectedRecord = useMemo(
    () =>
      normalizedResults.find((record) => record._record_id === selectedRecordId) || null,
    [normalizedResults, selectedRecordId]
  );
  const detailModel = selectedRecordId ? detailModelsById[selectedRecordId] || null : null;
  const showRightDetailPanel =
    isDetailPanelOpen && detailPanelPosition === DETAIL_PANEL_POSITIONS.RIGHT;
  const showTopDetailPanel =
    isDetailPanelOpen && detailPanelPosition === DETAIL_PANEL_POSITIONS.TOP;
  const datasetLabel =
    SEMANTIC_DATASETS.find((dataset) => dataset.id === activeTab)?.name ||
    activeConfig.label ||
    activeTab;

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      notifyAndRedirectToLogin({
        pathname: "/research",
        message: "Please log in to continue.",
        clearAuth: false,
      });
    }
  }, []);

  useEffect(() => () => searchAbortControllerRef.current?.abort(), []);

  useEffect(() => {
    if (!selectedRecordId) {
      setIsDetailPanelOpen(false);
    }
  }, [selectedRecordId, setIsDetailPanelOpen]);

  useEffect(() => {
    if (!showTopDetailPanel || typeof window === "undefined") {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsDetailPanelOpen(false);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showTopDetailPanel, setIsDetailPanelOpen]);

  const clearSelectionAndDetail = () => {
    setSelectedRowIds([]);
    setSelectedRecordId(null);
    setIsDetailPanelOpen(false);
  };

  const handleSearch = async (event) => {
    event.preventDefault();

    const trimmedQuery = searchQuery.trim();
    if (!trimmedQuery) {
      setError("Please enter a search query");
      return;
    }

    if (!authService.isAuthenticated()) {
      setError("Please log in to search");
      notifyAndRedirectToLogin({
        pathname: "/research",
        message: "Please log in to continue.",
        clearAuth: false,
      });
      return;
    }

    setLoading(true);
    setError("");
    setSubmittedQuery(trimmedQuery);

    searchAbortControllerRef.current?.abort();
    const controller = new AbortController();
    searchAbortControllerRef.current = controller;
    const requestId = latestSearchRequestRef.current + 1;
    latestSearchRequestRef.current = requestId;

    try {
      const response = await researchService.semanticSearch(
        {
          query: trimmedQuery,
          dataset: activeTab,
          top_k: 25,
        },
        {
          signal: controller.signal,
        }
      );

      if (latestSearchRequestRef.current !== requestId) {
        return;
      }

      if (!response || typeof response !== "object") {
        setError("Invalid response from server");
        return;
      }

      if (response.status === "success" && response.data) {
        setSearchResults((previous) => ({
          ...previous,
          [activeTab]: response.data.results || [],
        }));
        clearSelectionAndDetail();
      } else {
        setError(response.message || response.detail || "Search failed");
      }
    } catch (err) {
      if (err.name === "AbortError") {
        return;
      }

      setError(err.message || "Search failed. Please try again.");
    } finally {
      if (latestSearchRequestRef.current === requestId) {
        setLoading(false);
        searchAbortControllerRef.current = null;
      }
    }
  };

  const handleTabChange = (datasetId) => {
    searchAbortControllerRef.current?.abort();
    setLoading(false);
    setError("");
    setActiveTab(datasetId);
    clearSelectionAndDetail();
  };

  const handleRowClick = (recordId) => {
    setSelectedRecordId(recordId);
    setIsDetailPanelOpen(true);
  };

  const handleDownloadSelected = async () => {
    if (!selectedRows.length) {
      return;
    }

    try {
      const dataset = SEMANTIC_DATASETS.find((item) => item.id === activeTab);
      const results = selectedRows.map((record) => getDownloadableResultFromNormalized(record));
      await researchService.downloadSearchResults({
        results,
        dataset: dataset?.name || activeConfig.label || activeTab,
        format: "csv",
      });
    } catch (err) {
      setError(`Download failed: ${err.message}`);
    }
  };

  const handleDownloadSingle = async () => {
    if (!selectedRecord) {
      return;
    }

    try {
      await researchService.downloadSingleResult(
        getDownloadableResultFromNormalized(selectedRecord),
        activeTab
      );
    } catch (err) {
      setError(`Download failed: ${err.message}`);
    }
  };

  return (
    <div className="flex min-h-screen flex-col overflow-x-hidden bg-[#090d14] text-slate-100">
      <Header />

      <main className="flex-1">
        <div className="mx-auto flex w-full max-w-[1480px] flex-col gap-8 px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-slate-50 sm:text-4xl">Research Dashboard</h1>
            <p className="mt-2 text-sm text-slate-400 sm:text-base">
              Search across datasets with AI-powered semantic matching
            </p>
          </div>

          <section className="overflow-hidden rounded-[1.8rem] border border-slate-800/80 bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.10),_transparent_30%),radial-gradient(circle_at_top_right,_rgba(16,185,129,0.10),_transparent_28%),linear-gradient(180deg,_rgba(15,23,42,0.98),_rgba(6,10,18,0.99))] p-6 shadow-[0_30px_90px_rgba(2,6,23,0.72)] backdrop-blur sm:p-8">
            <form onSubmit={handleSearch} className="flex flex-col gap-4">
              <div className="flex flex-col gap-3 rounded-xl border border-blue-500/30 bg-slate-800/80 p-3 shadow-[0_4px_16px_rgba(59,130,246,0.1)] backdrop-blur-[10px] sm:gap-4 sm:p-4 md:flex-row md:items-center md:px-5">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <FaSearch className="shrink-0 text-lg text-blue-500 sm:text-xl" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder={`Search inside ${datasetLabel}`}
                    className="min-w-0 flex-1 bg-transparent py-2 text-base text-slate-50 outline-none placeholder:text-slate-500"
                  />
                </div>
                <button
                  type="submit"
                  className="inline-flex min-h-[3.5rem] w-full shrink-0 items-center justify-center gap-2 rounded-2xl bg-white px-5 text-sm font-semibold text-slate-950 transition-all hover:-translate-y-0.5 hover:bg-slate-100 disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-400 disabled:shadow-none disabled:hover:translate-y-0 disabled:hover:bg-slate-700 md:w-auto"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/35 border-t-white" />
                      Searching...
                    </>
                  ) : (
                    "Search"
                  )}
                </button>
              </div>
            </form>

            {error ? (
              <div className="mt-4 rounded-lg border border-red-500/40 bg-red-500/20 px-5 py-3 text-sm text-red-300">
                {error}
              </div>
            ) : null}

            <div className="mt-6 flex w-full flex-col gap-4 lg:flex-row lg:items-start">
              <div className="min-w-0 flex-1">
                <DatasetTabs
                  datasets={SEMANTIC_DATASETS}
                  activeDataset={activeTab}
                  configByDataset={datasetViewConfig}
                  onChange={handleTabChange}
                  disabled={loading}
                />
              </div>
              <DataViewDetailPanelToggle
                detailPanelPosition={detailPanelPosition}
                detailPanelPositions={DETAIL_PANEL_POSITIONS}
                onChange={setDetailPanelPosition}
              />
            </div>
          </section>

          {currentResults.length > 0 ? (
            <>
              <section className="rounded-2xl border border-slate-800/80 bg-slate-900/70 p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex flex-wrap items-center gap-3 text-xs text-slate-300 sm:text-sm">
                    <span className="rounded-full border border-slate-700 px-3 py-1">
                      Results: <strong>{currentResults.length}</strong>
                    </span>
                 
                  </div>
                  <button
                    type="button"
                    onClick={handleDownloadSelected}
                    disabled={!selectedRows.length}
                    className="inline-flex items-center justify-center rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-100 transition-colors hover:border-emerald-400 hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <FaDownload className="mr-2" />
                    Download selected
                  </button>
                </div>
              </section>

              <div
                className={
                  showRightDetailPanel
                    ? "grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(22rem,0.95fr)]"
                    : "grid gap-6"
                }
              >
                <div className="min-w-0 space-y-6">
                  <DatasetRecordTable
                    totalRecords={currentResults.length}
                    totalIsEstimated={false}
                    summaryConfig={activeConfig.summary}
                    theme={activeConfig.theme}
                    rows={rowModels}
                    selectedRecordId={selectedRecordId}
                    enableRowSelection
                    selectedRowIds={selectedRowIds}
                    onToggleRowSelection={toggleRow}
                    onToggleSelectAllRows={toggleAll}
                    onRowClick={handleRowClick}
                    loading={loading}
                    error={error}
                    query={submittedQuery}
                  />
                </div>

                {showRightDetailPanel ? (
                  <div className="min-w-0 xl:sticky xl:top-24 xl:self-start">
                    <RecordDetailCard
                      datasetKey={activeTab}
                      datasetLabel={activeConfig.label}
                      detailModel={detailModel}
                      editableRecord={selectedRecord}
                      theme={activeConfig.theme}
                      loading={loading}
                      error={error}
                      onClose={() => setIsDetailPanelOpen(false)}
                      onDownload={handleDownloadSingle}
                      canEdit={false}
                      downloadDisabled={!selectedRecord}
                      downloadLabel="Download Record CSV"
                    />
                  </div>
                ) : null}
              </div>
            </>
          ) : null}

          {!loading && currentResults.length === 0 && submittedQuery ? (
            <div className="rounded-xl border border-blue-500/20 bg-slate-800/40 px-5 py-14 text-center text-slate-400">
              <p className="text-sm text-slate-500">Click search to get data for this dataset</p>
            </div>
          ) : null}

          {!submittedQuery ? (
            <div className="rounded-xl border border-blue-500/20 bg-slate-800/40 px-5 py-14 text-center text-slate-400">
              <h3 className="text-xl font-semibold text-slate-200">Welcome to Enhanced Research Search</h3>
              <p className="mt-2">Enter a search query above and select a dataset to begin</p>
            </div>
          ) : null}
        </div>
      </main>

      {showTopDetailPanel ? (
        <div
          className="fixed inset-0 z-40 flex items-start justify-center bg-slate-950/72 px-4 pb-6 pt-20 backdrop-blur-sm sm:px-6 lg:px-8"
          onClick={() => setIsDetailPanelOpen(false)}
          role="presentation"
        >
          <div
            className="max-h-[calc(100vh-7rem)] w-full max-w-[1120px] overflow-y-auto"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-3 flex justify-end">
              <div className="rounded-full border border-slate-700/80 bg-slate-950/90 px-3 py-1 text-[11px] font-medium text-slate-400 shadow-[0_10px_30px_rgba(2,6,23,0.45)]">
                Press <span className="font-semibold text-slate-200">Esc</span> to close
              </div>
            </div>
            <RecordDetailCard
              datasetKey={activeTab}
              datasetLabel={activeConfig.label}
              detailModel={detailModel}
              editableRecord={selectedRecord}
              theme={activeConfig.theme}
              loading={loading}
              error={error}
              onClose={() => setIsDetailPanelOpen(false)}
              onDownload={handleDownloadSingle}
              canEdit={false}
              downloadDisabled={!selectedRecord}
              downloadLabel="Download Record CSV"
            />
          </div>
        </div>
      ) : null}

      <Footer />
    </div>
  );
};

export default EnhancedResearchDashboard;
